import React, { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { AlertTriangle, Loader2, ArrowRight, XCircle, FileText, CheckCircle2 } from "lucide-react";

/* Dir 148 — the Stage-1 ADVISORY BRIEF in the cockpit. Instead of a silent hard-stop, Anvil TELLS the operator
   what the opportunity is (notice type), whether it's a go and why, and what's attached vs missing — then
   offers Proceed / Decline. Proceeding never unlocks fabrication: on a non-biddable notice it does the
   honest-partial work (fills the attached form(s)) and stops. What's missing is shown here, never in a file. */
const recoClass = (r) =>
  r === "GO" ? "bg-emerald-600 text-white" : r === "NO-GO" ? "bg-red-600 text-white" : "bg-amber-500 text-white";

export function Stage1Brief({ deal, onDone }) {
  const b = deal.stage1_brief || {};
  const isPartial = deal.engine_status === "partial";
  const [busy, setBusy] = useState("");
  const [note, setNote] = useState("");
  const [showProceed, setShowProceed] = useState(false);

  const act = async (kind) => {
    setBusy(kind);
    try {
      const r = await api.post(`/bids/${deal.id}/stage1/${kind}`, { reason: note.trim() });
      toast.success(kind === "proceed"
        ? (r.data.resumed ? "Proceeding — Anvil is doing the work that's possible." : "Proceeding.")
        : "Marked NO-BID.");
      onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Action failed.");
    } finally {
      setBusy("");
    }
  };

  return (
    <div className={`mb-5 rounded-lg border p-4 sm:p-5 ${isPartial ? "border-emerald-300 bg-emerald-50 dark:bg-emerald-950/20 dark:border-emerald-800" : "border-amber-300 bg-amber-50 dark:bg-amber-950/20 dark:border-amber-800"}`}
      data-testid="stage1-brief">
      <div className="flex items-start gap-3">
        <div className={`h-9 w-9 rounded-md flex items-center justify-center shrink-0 ${isPartial ? "bg-emerald-100 dark:bg-emerald-900/40" : "bg-amber-100 dark:bg-amber-900/40"}`}>
          {isPartial ? <CheckCircle2 className="h-5 w-5 text-emerald-700 dark:text-emerald-400" />
            : <AlertTriangle className="h-5 w-5 text-amber-700 dark:text-amber-400" />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="text-sm font-semibold text-zinc-900 dark:text-white">
              {isPartial ? "Attached form(s) filled — no bid was fabricated" : "Stage-1 brief — your call before Anvil goes further"}
            </h3>
            {b.notice_type && (
              <span data-testid="stage1-notice-type" className={`text-[10px] font-semibold px-2 py-0.5 rounded ${b.biddable ? "bg-blue-600 text-white" : "bg-amber-500 text-white"}`}>
                {b.notice_type}
              </span>
            )}
            {b.recommendation && (
              <span data-testid="stage1-reco" className={`text-[10px] font-bold px-2 py-0.5 rounded ${recoClass(b.recommendation)}`}>{b.recommendation}</span>
            )}
          </div>

          <dl className="mt-3 grid grid-cols-[auto,1fr] gap-x-3 gap-y-1 text-xs">
            {[["Agency", b.agency], ["Place of performance", b.place_of_performance], ["Set-aside", b.set_aside], ["Deadline", b.deadline]].map(([k, v]) => (
              <React.Fragment key={k}><dt className="text-zinc-400">{k}</dt><dd className="text-zinc-800 dark:text-zinc-200">{v || "—"}</dd></React.Fragment>
            ))}
          </dl>

          {b.showstoppers?.length > 0 && (
            <div className="mt-3">
              <div className="text-[11px] font-semibold uppercase tracking-wide text-amber-600 mb-1">Why it's a {b.recommendation}</div>
              <ul className="list-disc pl-4 space-y-1 text-xs text-zinc-700 dark:text-zinc-300" data-testid="stage1-showstoppers">
                {b.showstoppers.map((s, i) => <li key={i}>{s}</li>)}
              </ul>
            </div>
          )}

          <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <div className="text-[11px] font-semibold uppercase tracking-wide text-zinc-400 mb-1 flex items-center gap-1"><FileText className="h-3 w-3" /> Attached</div>
              {b.attached?.length ? (
                <ul className="space-y-0.5 text-xs text-zinc-700 dark:text-zinc-300" data-testid="stage1-attached">{b.attached.map((a, i) => <li key={i} className="truncate" title={a}>• {a}</li>)}</ul>
              ) : <div className="text-xs text-zinc-400 italic">None retrieved.</div>}
            </div>
            <div>
              <div className="text-[11px] font-semibold uppercase tracking-wide text-zinc-400 mb-1">Missing to bid</div>
              {b.missing_to_bid?.length ? (
                <ul className="space-y-0.5 text-xs text-zinc-700 dark:text-zinc-300" data-testid="stage1-missing">{b.missing_to_bid.map((a, i) => <li key={i}>• {a}</li>)}</ul>
              ) : <div className="text-xs text-zinc-400 italic">—</div>}
            </div>
          </div>

          {b.narrative && (
            <p className="mt-3 text-[13px] text-zinc-700 dark:text-zinc-300 leading-relaxed whitespace-pre-wrap border-t border-amber-200/60 dark:border-amber-800/40 pt-3" data-testid="stage1-narrative">{b.narrative}</p>
          )}

          {isPartial ? (
            <div className="mt-4 text-xs text-emerald-800 dark:text-emerald-300" data-testid="stage1-partial-note">
              You proceeded past a non-biddable notice. Anvil filled the attached government form(s) from WD's profile —
              nothing else was invented. The <strong>Submission Package</strong> contains only those filled form(s).
            </div>
          ) : deal.engine_status === "advisory" ? (
            <>
              {showProceed && (
                <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2}
                  placeholder="Optional: why proceed? (recorded on the deal)" data-testid="stage1-reason-input"
                  className="mt-4 w-full text-[13px] px-3 py-2 rounded-md border border-amber-300 dark:border-amber-800 bg-white dark:bg-zinc-900 text-zinc-800 dark:text-zinc-100 focus:outline-none focus:ring-1 focus:ring-amber-400" />
              )}
              <div className="mt-3 flex items-center gap-2 flex-wrap">
                {!showProceed ? (
                  <button onClick={() => setShowProceed(true)} disabled={!!busy} data-testid="stage1-proceed-btn"
                    className="flex items-center gap-2 text-xs font-semibold bg-amber-600 hover:bg-amber-500 text-white px-4 py-2 rounded-md disabled:opacity-50">
                    <ArrowRight className="h-4 w-4" /> Proceed
                  </button>
                ) : (
                  <button onClick={() => act("proceed")} disabled={!!busy} data-testid="stage1-proceed-confirm-btn"
                    className="flex items-center gap-2 text-xs font-semibold bg-amber-600 hover:bg-amber-500 text-white px-4 py-2 rounded-md disabled:opacity-50">
                    {busy === "proceed" ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRight className="h-4 w-4" />} Confirm — pursue
                  </button>
                )}
                <button onClick={() => act("decline")} disabled={!!busy} data-testid="stage1-decline-btn"
                  className="flex items-center gap-2 text-xs font-semibold bg-white dark:bg-zinc-800 border border-amber-300 dark:border-amber-800 text-amber-800 dark:text-amber-300 px-4 py-2 rounded-md hover:bg-amber-100 dark:hover:bg-zinc-700 disabled:opacity-50">
                  {busy === "decline" ? <Loader2 className="h-4 w-4 animate-spin" /> : <XCircle className="h-4 w-4" />} Decline (NO-BID)
                </button>
              </div>
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
}
